import { useCallback, useContext } from "react";
import { UI } from "@wwimmo/ui";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import styles from "./InvoiceAccountings.module.css";
import { RealestateField } from "./accounting/RealestateField";
import { InvoiceAccounting } from "./accounting/InvoiceAccounting";
import { ErpType } from "src/network/User";
import { LOADING_TYPE, LOADING_TYPE_ACCOUNTING } from "src/stores/krediflow/InvoiceEnums";
import { InvoiceAccountingsReadOnlyView } from "./readonly/InvoiceAccountingsReadOnly";
import { PayoutAccountIT2Field } from "./accounting/PayoutAccountFieldIT2";

const InvoiceAccoutingsBase = () => {
    const { t } = useTranslation();
    const { invoiceStore, authStore } = useContext(RootStoreContext);

    const onChangeAccountingDate = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            let newValue: any = e.target.value;

            invoiceStore.updateAccountingWithBookingDate(newValue);
        },
        [invoiceStore]
    );

    const addAccountingRow = useCallback(() => {
        invoiceStore.currentInvoice?.addAccountingRow();
    }, [invoiceStore]);

    const saveAccountings = useCallback(
        (closeEditModeAfterSave: boolean = true) =>
            async (e: any) => {
                if (invoiceStore.isAccountingEditing) {
                    if (e) {
                        e.preventDefault();
                    }

                    const accountingsSuccessfullyValidated = invoiceStore.currentInvoice?.validateAccountings();

                    if (accountingsSuccessfullyValidated) {
                        await invoiceStore.saveCurrentInvoiceAccountings(closeEditModeAfterSave);
                        invoiceStore.currentInvoice?.setValidatingAccountingsOnFieldLevel(false);
                    } else {
                        invoiceStore.currentInvoice?.setValidatingAccountingsOnFieldLevel(true);
                    }
                }
            },
        [invoiceStore]
    );

    const addAccountingButtonLabel = (
        <div className="d-flex align-items-center">
            <UI.Icon icon={UI.SVGIcon.Plus} color={"white"} size={25} />
            <div>{t("screens.kredi_flow.accountings.add_accounting")}</div>
        </div>
    );

    const saveInvoicesButtonLabel = (
        <div className="d-flex align-items-center">
            <div>{t("labels.save")}</div>
            <UI.RotatingSpinner
                className={`${styles.AccountingSaveButtonSpinner} ${
                    invoiceStore.loadingTypeAccounting !== LOADING_TYPE_ACCOUNTING.SAVING_ACCOUNTINGS ? "d-none" : ""
                }`}
                noLogo={true}
                size={20}
            />
        </div>
    );

    const saveAccountingsButton = (
        <UI.Button
            onClick={saveAccountings(true)}
            className={styles.SaveButton}
            label={saveInvoicesButtonLabel}
            disabled={invoiceStore.loadingTypeAccounting !== LOADING_TYPE_ACCOUNTING.NONE}
        />
    );

    const onDiscardAccountingChanges = useCallback(() => {
        invoiceStore.discardCurrentInvoiceAccountingsChanges();
    }, [invoiceStore]);

    const onClickEditButton = useCallback(() => {
        invoiceStore.setIsAccountingEditing(true);
    }, [invoiceStore]);

    const titleRow = (
        <UI.Row>
            <div className={`${styles.InvoiceAccountingsTitle} mb-3`}>
                {t("screens.kredi_flow.accountings.section_title")}
            </div>
            <div className="ml-auto" style={{ paddingRight: "15px" }}>
                {invoiceStore.isAccountingEditing ? (
                    saveAccountingsButton
                ) : (
                    <UI.Button
                        onClick={onClickEditButton}
                        className={`${styles.EditButton}`}
                        label={t("labels.edit")}
                    />
                )}
            </div>
        </UI.Row>
    );

    const accountingsEditViewRows = (
        <>
            <UI.Row>
                <UI.Col lg={2}>
                    <UI.Input
                        label={`${t("screens.kredi_flow.accountings.date").toString()} *`}
                        type="date"
                        autoComplete="off"
                        value={
                            invoiceStore.currentInvoice && invoiceStore.currentInvoice.accountingDate
                                ? invoiceStore.currentInvoice.accountingDate
                                : ""
                        }
                        onChange={onChangeAccountingDate}
                    />
                </UI.Col>
            </UI.Row>
            {authStore.user?.erpType === ErpType.IT2 ? (
                <UI.Row>
                    <UI.Col lg={5}>
                        <RealestateField />
                    </UI.Col>
                    <UI.Col
                        lg={5}
                        className={invoiceStore.currentInvoice?.canSeeAccountingPayoutAccountField() ? "" : "d-none"}
                    >
                        <PayoutAccountIT2Field />
                    </UI.Col>
                </UI.Row>
            ) : (
                <></>
            )}

            {/* ACCOUNTING ROWS */}
            {invoiceStore.currentInvoice &&
            invoiceStore.currentInvoice.accountings &&
            invoiceStore.currentInvoice.accountings.length > 0
                ? invoiceStore.currentInvoice.accountings.map((accounting, index) => (
                      <InvoiceAccounting key={index} accounting={accounting} accountingIndex={index} />
                  ))
                : null}

            {!invoiceStore.currentInvoice?.totalAccountingAmountMatchesInvoiceAmount ? (
                <div className={`error-text ${styles.AccountingAmountErrorMessage}`}>
                    <span>{t("screens.kredi_flow.accountings.error.accounting_amounts_dont_match")}</span>
                </div>
            ) : (
                <></>
            )}

            <UI.Row>
                <UI.Button
                    onClick={addAccountingRow}
                    className={`${styles.AddAccountingRowButton} default-button-height my-4 ml-2`}
                    label={addAccountingButtonLabel}
                />
            </UI.Row>
            <UI.Row className={`${styles.AccountingsTotalAmountRow} align-content-center`}>
                <UI.Col lg={{ span: 2, offset: 10 }}>
                    <div className={"d-flex"}>
                        <div className={styles.AccountingsTotalAmountText} style={{ width: "84%" }}>
                            <span>{t("screens.kredi_flow.total")}</span>
                            <span className="ml-3">{invoiceStore.currentInvoice?.amount?.toFixed(2)}</span>
                        </div>
                        <div style={{ width: "16%" }}></div>
                    </div>
                </UI.Col>
            </UI.Row>
            <UI.Row className="mt-3">
                <UI.Col className={"flex-grow-0"}>{saveAccountingsButton}</UI.Col>
                <UI.Col>
                    <UI.Button
                        label={t("labels.discard")}
                        disabled={
                            !invoiceStore.currentInvoice?.hasChangesAccounting ||
                            invoiceStore.loadingTypeAccounting === LOADING_TYPE_ACCOUNTING.SAVING_ACCOUNTINGS
                        }
                        onClick={onDiscardAccountingChanges}
                        className={`${styles.DiscardButton} default-button-height`}
                        variant="outline-primary"
                    />
                </UI.Col>
            </UI.Row>
        </>
    );

    return invoiceStore.loadingType === LOADING_TYPE.INITIALIZING ? (
        <div>
            <UI.RotatingSpinner noLogo size={40} />
            <div className="mt-3 text-center">{t("screens.kredi_flow.loading_accountings")}</div>
        </div>
    ) : (
        <>
            {titleRow}
            {invoiceStore.isAccountingEditing ? accountingsEditViewRows : <InvoiceAccountingsReadOnlyView />}
        </>
    );
};

export const InvoiceAccountings = Sentry.withProfiler(observer(InvoiceAccoutingsBase));
